// src/app/modules/Devices/components/SettingsTab.jsx
import React, { useEffect, useState } from 'react';
import { Save, Trash2, Power } from 'lucide-react';
import { toast } from 'react-toastify';
import { devicesApi, errorMessage } from '../api';
import { SCAN_MODES } from '../utils';
import { Button, DvModal } from './ui';

const formFrom = (d) => ({
  name: d.name || '',
  location_id: d.location_id || '',
  scan_mode: d.settings?.scan_mode || 'toggle',
  repeat_scan_cooldown_s: d.settings?.repeat_scan_cooldown_s ?? 120,
  registration_ttl_s: d.settings?.registration_ttl_s ?? 300,
  heartbeat_interval_s: d.settings?.heartbeat_interval_s ?? 1800,
});

export function SettingsTab({ orgId, device, locations, canManage, onSaved }) {
  const [form, setForm] = useState(() => formFrom(device));
  const [saving, setSaving] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeName, setWipeName] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);
  const deviceName = device.name || device.device_id;

  // Reset the form when switching devices, not on every background refresh
  useEffect(() => { setForm(formFrom(device)); }, [device.device_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  // Compare as strings: inputs hold strings, the device holds numbers
  const norm = (f) => JSON.stringify(Object.fromEntries(Object.entries(f).map(([k, v]) => [k, String(v)])));
  const dirty = norm(form) !== norm(formFrom(device));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await devicesApi.update(orgId, device.device_id, {
        name: form.name.trim() || null,
        location_id: form.location_id || null,
        settings: {
          scan_mode: form.scan_mode,
          repeat_scan_cooldown_s: Number(form.repeat_scan_cooldown_s),
          registration_ttl_s: Number(form.registration_ttl_s),
          heartbeat_interval_s: Number(form.heartbeat_interval_s),
        },
      });
      toast.success('Device settings saved');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save settings'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    setStatusBusy(true);
    try {
      const next = device.status === 'active' ? 'disabled' : 'active';
      await devicesApi.update(orgId, device.device_id, { status: next });
      toast.success(next === 'disabled' ? 'Device disabled — it can no longer send scans' : 'Device re-enabled');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setStatusBusy(false);
    }
  };

  const wipe = async () => {
    setStatusBusy(true);
    try {
      await devicesApi.wipe(orgId, device.device_id, wipeName);
      toast.success('Delete-all queued — it runs the next time someone presses the device button');
      setWipeOpen(false);
      setWipeName('');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setStatusBusy(false);
    }
  };

  const disabled = !canManage;
  return (
    <div className="dv-card__body">
      <form className="dv-form" onSubmit={save}>
        <label className="dv-field">
          <span>Device name</span>
          <input value={form.name} onChange={set('name')} maxLength={80} placeholder="e.g. Front gate" disabled={disabled} />
        </label>

        <label className="dv-field">
          <span>Location</span>
          <select value={form.location_id} onChange={set('location_id')} disabled={disabled}>
            <option value="">No location</option>
            {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
          </select>
          <small>Shown on attendance records as where the person clocked in.</small>
        </label>

        <div className="dv-field">
          <span>What a scan does</span>
          <div className="dv-radio-cards">
            {Object.entries(SCAN_MODES).map(([value, m]) => (
              <label key={value} className={`dv-radio-card${form.scan_mode === value ? ' checked' : ''}`}>
                <input type="radio" name="scan_mode" value={value} checked={form.scan_mode === value} onChange={set('scan_mode')} disabled={disabled} />
                <div><strong>{m.label}</strong><span>{m.hint}</span></div>
              </label>
            ))}
          </div>
        </div>

        <label className="dv-field">
          <span>Ignore repeat scans within (seconds)</span>
          <input type="number" min={0} max={3600} value={form.repeat_scan_cooldown_s} onChange={set('repeat_scan_cooldown_s')} disabled={disabled} />
          <small>Stops a double tap from checking someone in and straight back out.</small>
        </label>

        <label className="dv-field">
          <span>Enrolment window (seconds)</span>
          <input type="number" min={60} max={1800} value={form.registration_ttl_s} onChange={set('registration_ttl_s')} disabled={disabled} />
          <small>How long “Enrol a fingerprint” waits for someone to press the device button.</small>
        </label>

        <label className="dv-field">
          <span>Status report interval (seconds)</span>
          <input type="number" min={60} max={86400} value={form.heartbeat_interval_s} onChange={set('heartbeat_interval_s')} disabled={disabled} />
          <small>The device only reports when it is already online, so it may go quiet for longer when unused.</small>
        </label>

        {canManage && (
          <div><Button type="submit" variant="primary" icon={Save} loading={saving} disabled={!dirty}>Save changes</Button></div>
        )}
      </form>

      {canManage && (
        <>
          <h3 className="dv-section-title" style={{ marginTop: 28 }}>Danger zone</h3>
          <div className="dv-danger-zone" style={{ marginTop: 10, maxWidth: 560 }}>
            <div className="dv-danger-row">
              <div>
                <strong>{device.status === 'active' ? 'Disable device' : 'Re-enable device'}</strong>
                <span>{device.status === 'active' ? 'Use if the device is lost or stolen. It stops being able to send anything.' : 'Allow this device to connect again.'}</span>
              </div>
              <Button variant="danger" icon={Power} loading={statusBusy && !wipeOpen} onClick={toggleStatus}>
                {device.status === 'active' ? 'Disable' : 'Enable'}
              </Button>
            </div>
            <div className="dv-danger-row">
              <div>
                <strong>Delete all fingerprints</strong>
                <span>Removes every fingerprint from the sensor. Attendance history is kept.</span>
              </div>
              <Button variant="danger" icon={Trash2} onClick={() => setWipeOpen(true)}>Delete all</Button>
            </div>
          </div>
        </>
      )}

      <DvModal
        show={wipeOpen}
        onHide={() => setWipeOpen(false)}
        title="Delete all fingerprints?"
        subtitle={deviceName}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setWipeOpen(false)}>Cancel</Button>
            <Button
              variant="danger-solid"
              loading={statusBusy}
              disabled={wipeName.trim().toLowerCase() !== deviceName.trim().toLowerCase()}
              onClick={wipe}
            >
              Delete all
            </Button>
          </>
        )}
      >
        <p style={{ margin: 0 }}>Everyone enrolled on this device will need to enrol again. This runs the next time someone presses the device button.</p>
        <label className="dv-field">
          <span>Type <b>{deviceName}</b> to confirm</span>
          <input value={wipeName} onChange={(e) => setWipeName(e.target.value)} autoFocus />
        </label>
      </DvModal>
    </div>
  );
}

export default SettingsTab;
